<template>
  <v-container>
    <v-data-table
      :headers="filteredHeaders"
      item-value="id"
      class="mt-2"
      hide-default-footer
      :loading="loading"
      loading-text="Loading... Please wait"
      :sort-by.sync="sortBy"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>Sets</v-toolbar-title>
        </v-toolbar>
      </template>

      <template v-slot:body>
        <draggable
          v-model="localSets"
          :tag="'tbody'"
          handle=".drag-handle"
          @end="onReorder"
        >
          <tr v-for="(item, index) in localSets" :key="item.id">
            <td v-for="header in filteredHeaders" :key="header.value">
              <div v-if="header.value === 'videoUrl' && item.videoUrl">
                <v-icon color="blue" @click="openVideo(item.videoUrl)">
                  play_circle
                </v-icon>
              </div>
              <v-edit-dialog
                v-else
                :return-value.sync="item[header.value]"
                @save="onSaveEdit(item, header.value)"
                @cancel="onCancelEdit(item, header.value)"
              >
                <span>{{ item[header.value] }}</span>
                <v-icon
                  v-if="header.value === 'drag'"
                  class="drag-handle"
                  color="grey"
                >
                  drag_indicator
                </v-icon>
                <template #input>
                  <v-text-field
                    v-model="item[header.value]"
                    single-line
                    dense
                  ></v-text-field>
                </template>
              </v-edit-dialog>
            </td>

            <!-- Actions Column -->
            <td>
              <v-btn icon color="blue" @click="$emit('edit-set', item)">
                <v-icon>edit</v-icon>
              </v-btn>
              <v-btn icon color="red" @click="$emit('delete-set', item)">
                <v-icon>delete</v-icon>
              </v-btn>
            </td>
          </tr>

          <!-- Add New Set Row -->
          <tr>
            <td v-for="header in filteredHeaders" :key="header.value">
              <v-edit-dialog
                v-model="newSet[header.value]"
                @save="$emit('edit-set', newSet)"
              >
                <v-text-field
                  v-if="header.value !== 'actions' && header.value !== 'drag'"
                  v-model="newSet[header.value]"
                  single-line
                  dense
                  :placeholder="header.text"
                ></v-text-field>
              </v-edit-dialog>
            </td>

            <!-- Add Button -->
            <td>
              <v-btn color="green" icon @click="addSet(newSet)">
                <v-icon>add</v-icon>
              </v-btn>
            </td>
          </tr>
        </draggable>
      </template>
    </v-data-table>

    <!-- Reusable Video Dialog -->
    <VideoDialog
      v-if="videoUrl"
      :videoUrl="videoUrl"
      @close="videoUrl = null"
    />
  </v-container>
</template>

<script>
import draggable from "vuedraggable";
import { mapGetters } from "vuex";
import VideoDialog from "@/components/shared/video_dialog.vue";

export default {
  name: "SetsTable",
  components: {
    draggable,
    VideoDialog,
  },
  props: {
    sets: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortBy: "order",
      videoUrl: null,
      localSets: [...this.sets], // Use local state to avoid mutating prop
      newSet: {
        setName: "",
        setDesc: "",
        setPurpose: "",
        noOfSets: null,
        noOfRep: null,
        distance: null,
        distanceSum: null,
        stroke: "",
        setInterval: null,
        timePerDist: null,
        timeTotal: null,
        tag: "",
        equipment: "",
        videoUrl: "",
      },
      headers: [
        { text: "Drag", value: "drag", sortable: false },
        { text: "Set Name", value: "setName" },
        { text: "Description", value: "setDesc" },
        { text: "Purpose", value: "setPurpose" },
        { text: "No. of Sets", value: "noOfSets" },
        { text: "No. of Reps", value: "noOfRep" },
        { text: "Distance (m)", value: "distance" },
        { text: "Total Distance (m)", value: "distanceSum" },
        { text: "Stroke", value: "stroke" },
        { text: "Interval", value: "setInterval" },
        { text: "Time/Distance", value: "timePerDist" },
        { text: "Total Time", value: "timeTotal" },
        { text: "Tag", value: "tag" },
        { text: "Equipment", value: "equipment" },
        { text: "Order", value: "order" },
        { text: "VideoUrl", value: "videoUrl" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters(["user"]),
    filteredHeaders() {
      const visibleFields = new Set();
      this.localSets.forEach((set) => {
        for (const [key, value] of Object.entries(set)) {
          if (value !== null && value !== 0 && value !== "") {
            visibleFields.add(key);
          }
        }
      });
      visibleFields.add("actions");
      visibleFields.add("drag");
      return this.headers.filter((header) => visibleFields.has(header.value));
    },
  },
  watch: {
    sets: {
      handler(newVal) {
        this.localSets = [...newVal]; // Sync local data when parent updates
      },
      deep: true,
    },
  },
  methods: {
    getNextOrder() {
      return this.localSets.length > 0
        ? Math.max(...this.localSets.map((set) => set.order)) + 1
        : 1;
    },
    openVideo(videoUrl) {
      this.videoUrl = videoUrl;
    },
    onSaveEdit(item, field) {
      console.log(`Saved: ${field} = ${item[field]}`);
    },
    onCancelEdit(item, field) {
      console.log(`Edit cancelled for: ${field}`);
    },
    addSet(set) {
      console.log("getNextOrder:", this.getNextOrder());
      const newSetWithOrder = {
        ...this.newSet,
        order: this.getNextOrder(),
      };

      // Emit event to parent      
      this.$emit("add-set", newSetWithOrder);
    },
    onReorder() {
      console.log("Reordered:", this.localSets);

      // Update order property
      this.localSets = this.localSets.map((set, index) => ({
        ...set,
        order: index + 1,
      }));

      // Ensure Vue re-renders by replacing array reference
      this.localSets = [...this.localSets];

      // Emit updated sets to parent
      this.$emit("update-sets", [...this.localSets]);
    },
  },
};
</script>
